import { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useApp } from '../store/app';
import { requestCover, useCoverUrl, useCovers } from '../store/covers';
import { useDrive } from '../store/drive';
import { activateDrive } from '../lib/sources';
import { openBook } from '../player/engine';
import {
  allDurationsKnown,
  bookRatio,
  hasStarted,
  lastStarted,
  remainingSeconds,
  totalDuration,
} from '../lib/bookMath';
import { deviceLabel } from '../lib/localStore';
import { formatRemaining, formatTime } from '../lib/format';
import { searchShelf, shelfAt, type ShelfEntry } from '../lib/shelves';
import { SearchBar } from './SearchBar';
import { setArtSource } from '../lib/transition';
import { Cover, Equalizer, Button, Sheet, IconButton, ProgressLine } from './kit';
import { AlertIcon, BackIcon, BookIcon, CheckIcon, FolderIcon, PlayIcon } from './icons';
import type { Book, FolderRef } from '../types';

export function Library() {
  const books = useApp((s) => s.books);
  const status = useApp((s) => s.libraryStatus);
  const error = useApp((s) => s.libraryError);
  const navigate = useApp((s) => s.navigate);
  const folderId = useApp((s) => (s.route.name === 'library' ? (s.route.folderId ?? null) : null));
  const query = useApp((s) => (s.route.name === 'library' ? (s.route.q ?? '') : ''));
  const searching = query.trim() !== '';
  const shelf = useMemo(() => shelfAt(books, folderId), [books, folderId]);
  const results = useMemo(
    () => (searching ? searchShelf(books, folderId, query) : null),
    [books, folderId, query, searching],
  );

  // The first letter typed adds a history entry, so Back clears the search;
  // the rest replace it, so Back does not replay the typing.
  const setQuery = (q: string) =>
    navigate(
      { name: 'library', ...(folderId ? { folderId } : {}), ...(q ? { q } : {}) },
      { replace: query !== '' },
    );

  if (status === 'loading' && books.length === 0) {
    return (
      <>
        <ReconnectNotice />
        <LibrarySkeleton />
      </>
    );
  }

  if (status === 'error' && books.length === 0) {
    return (
      <Sheet className="notice notice--error">
        <AlertIcon size={22} />
        <div>
          <h2 className="notice__title">Could not load your library</h2>
          <p className="notice__body">{error}</p>
        </div>
      </Sheet>
    );
  }

  if (books.length === 0) {
    return (
      <Sheet className="notice">
        <BookIcon size={22} />
        <div>
          <h2 className="notice__title">No audiobooks found</h2>
          <p className="notice__body">
            Nothing in this folder looked like audio. Pick a different folder in Settings, or
            check that the files are one of mp3, m4a, m4b, aac, opus, ogg, flac or wav.
          </p>
        </div>
      </Sheet>
    );
  }

  if (!shelf) {
    return (
      <Sheet className="notice">
        <FolderIcon size={22} />
        <div>
          <h2 className="notice__title">
            {status === 'loading' ? 'Loading…' : 'That folder is no longer in your library'}
          </h2>
          {status !== 'loading' && (
            <button type="button" className="linkbtn" onClick={() => navigate({ name: 'library' })}>
              Back to library
            </button>
          )}
        </div>
      </Sheet>
    );
  }

  const shelfName = shelf.trail[shelf.trail.length - 1]?.name;
  const entries = results?.entries ?? shelf.entries;

  return (
    <>
      <ReconnectNotice />
      {error && (
        <Sheet className="notice notice--warn">
          <AlertIcon size={20} />
          <p className="notice__body">
            Showing your last saved library — refreshing failed: {error}
          </p>
        </Sheet>
      )}
      {shelf.trail.length > 0 && <ShelfHeader trail={shelf.trail} entries={shelf.entries} />}
      {shelf.trail.length === 0 && !searching && <ResumePanel />}
      <SearchBar
        value={query}
        placeholder={shelfName ? `Search ${shelfName}` : 'Search your library'}
        onChange={setQuery}
      />
      {searching && entries.length > 0 && (
        <p className="search__count" aria-live="polite">
          {entries.length} {entries.length === 1 ? 'result' : 'results'}
        </p>
      )}
      {searching && entries.length === 0 ?
        <Sheet className="notice">
          <BookIcon size={22} />
          <div>
            <h2 className="notice__title" aria-live="polite">
              Nothing matches “{query.trim()}”{shelfName ? ` in ${shelfName}` : ''}
            </h2>
            <button type="button" className="linkbtn" onClick={() => setQuery('')}>
              Clear search
            </button>
          </div>
        </Sheet>
      : <ul className="grid">
          {entries.map((entry, i) => (
            // --i drives the entrance stagger; capped so a large library does not
            // end with cards appearing seconds late.
            <li key={entryKey(entry)} style={{ '--i': Math.min(i, 14) } as React.CSSProperties}>
              {entry.kind === 'book' ?
                <BookCard book={entry.book} shelfName={shelfName} where={entry.folder} />
              : <CollectionCard folder={entry.folder} books={entry.books} />}
            </li>
          ))}
        </ul>
      }
    </>
  );
}

/** Shown when a Drive folder is configured but the Google session has lapsed.
 *  Rather than dropping you into the demo library with no explanation, this
 *  says what happened and reconnects in one click. */
function ReconnectNotice() {
  const needsSignIn = useDrive((s) => s.needsSignIn);
  const root = useDrive((s) => s.root);
  const status = useDrive((s) => s.status);

  if (!needsSignIn || !root) return null;

  return (
    <Sheet className="notice notice--warn">
      <AlertIcon size={20} />
      <div>
        <h2 className="notice__title">Signed out of Google</h2>
        <p className="notice__body">
          Showing the demo library. Reconnect to load “{root.name}” from Drive again.
        </p>
      </div>
      <Button
        className="btn--primary notice__action"
        onClick={() => void activateDrive(root)}
        disabled={status === 'connecting'}
      >
        {status === 'connecting' ? 'Connecting…' : 'Reconnect'}
      </Button>
    </Sheet>
  );
}

/**
 * The book you were part-way through, waiting at the top of the library when
 * nothing is loaded yet — which is what you most likely opened the app for.
 * Once a book is loaded the mini player has it instead.
 */
function ResumePanel() {
  const navigate = useApp((s) => s.navigate);
  const candidate = useApp(
    useShallow((s) => {
      if (s.currentBookId) return [null, null] as const;
      const found = lastStarted(s.books, s.progress.books);
      return [found?.book ?? null, found?.entry ?? null] as const;
    }),
  );
  const [book, entry] = candidate;
  const coverUrl = useCoverUrl(book);
  if (!book || !entry) return null;

  const remaining = remainingSeconds(book, entry);
  const approx = allDurationsKnown(book) ? '' : '~';
  const where =
    totalDuration(book) > 0 ? timeLeft(remaining, approx)
    : whereYouAre(book, entry.trackIndex, entry.positionSec);
  const elsewhere = entry.device !== deviceLabel() ? `Last played on ${entry.device}` : null;

  const open = (event: React.MouseEvent<HTMLElement>) => {
    setArtSource(event.currentTarget.querySelector<HTMLElement>('[data-art]'));
    navigate({ name: 'player', bookId: book.id });
    void openBook(book, { autoplay: false });
  };

  return (
    <section className="glass resume" aria-label="Continue listening">
      <button type="button" className="resume__open" onClick={open}>
        <span className="resume__cover" data-art={book.id}>
          <Cover title={book.title} url={coverUrl} />
        </span>
        <span className="resume__text">
          <span className="resume__title">{book.title}</span>
          {book.author && <span className="resume__author">{book.author}</span>}
          <span className="resume__progress">
            <ProgressLine ratio={bookRatio(book, entry)} />
            <span className="tnum">{where}</span>
          </span>
          {elsewhere && <span className="resume__device">{elsewhere}</span>}
        </span>
      </button>
      <button
        type="button"
        className="press resume__play"
        aria-label={`Resume ${book.title}`}
        title={`Resume ${book.title}`}
        onClick={() => void openBook(book, { autoplay: true })}
      >
        <PlayIcon size={26} />
        <span className="resume__play-label">Resume</span>
      </button>
    </section>
  );
}

function entryKey(entry: ShelfEntry): string {
  return entry.kind === 'book' ? entry.book.id : `folder:${entry.folder.id}`;
}

/** Title, count and the way back out of a folder. Deeper folders also get a
 *  breadcrumb, so every level above is one tap away. */
function ShelfHeader({ trail, entries }: { trail: FolderRef[]; entries: ShelfEntry[] }) {
  const navigate = useApp((s) => s.navigate);
  const here = trail[trail.length - 1];
  const parent = trail[trail.length - 2];
  const count = entries.reduce((n, e) => n + (e.kind === 'book' ? 1 : e.books.length), 0);

  const goTo = (folder: FolderRef | undefined) =>
    navigate(folder ? { name: 'library', folderId: folder.id } : { name: 'library' });

  return (
    <div className="shelfhead">
      <IconButton
        label={parent ? `Back to ${parent.name}` : 'Back to library'}
        onClick={() => goTo(parent)}
      >
        <BackIcon size={20} />
      </IconButton>
      <div className="shelfhead__text">
        {trail.length > 1 && (
          <nav className="shelfhead__crumbs" aria-label="Folders">
            <button type="button" className="linkbtn" onClick={() => goTo(undefined)}>
              Library
            </button>
            {trail.slice(0, -1).map((folder) => (
              <span key={folder.id}>
                <span aria-hidden="true"> / </span>
                <button type="button" className="linkbtn" onClick={() => goTo(folder)}>
                  {folder.name}
                </button>
              </span>
            ))}
          </nav>
        )}
        <h2 className="shelfhead__title">{here?.name}</h2>
        <span className="shelfhead__sub">
          {count} {count === 1 ? 'book' : 'books'}
        </span>
      </div>
    </div>
  );
}

/** A folder of books, shown as one tile. Its cover is a mosaic of the books
 *  inside when there are enough covers to fill one, otherwise a generated
 *  jacket carrying the folder name. */
function CollectionCard({ folder, books }: { folder: FolderRef; books: Book[] }) {
  const navigate = useApp((s) => s.navigate);
  const containsCurrent = useApp((s) => books.some((b) => b.id === s.currentBookId));
  const playing = useApp((s) => s.playing);
  const [finished, inProgress] = useApp(
    useShallow((s) => {
      let done = 0;
      let started = 0;
      for (const b of books) {
        const entry = s.progress.books[b.id];
        if (entry?.finished) done += 1;
        else if (hasStarted(entry)) started += 1;
      }
      return [done, started];
    }),
  );

  // The mosaic only ever needs four; read embedded art for no more than that.
  const source = useApp((s) => s.source);
  const embedded = useCovers((s) => s.urls);
  useEffect(() => {
    if (source) for (const b of books.slice(0, 4)) requestCover(b, source);
  }, [books, source]);
  const covers = books.flatMap((b) => {
    const url = b.coverUrl ?? embedded[b.id];
    return url ? [url] : [];
  }).slice(0, 4);

  const status = [
    `${books.length} books`,
    inProgress > 0 ? `${inProgress} in progress` : null,
    finished > 0 ? `${finished} finished` : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <button
      type="button"
      onClick={() => navigate({ name: 'library', folderId: folder.id })}
      className="tile press bookcard bookcard--pile"
      data-current={containsCurrent || undefined}
    >
      <span className="bookcard__frame">
        {covers.length === 4 ?
          <span className="mosaic">
            {covers.map((url, i) => (
              <Cover key={i} title="" url={url} />
            ))}
          </span>
        : <Cover title={folder.name} />}
        {containsCurrent && <Equalizer playing={playing} />}
        <span className="bookcard__count">
          <FolderIcon size={14} />
          {books.length}
        </span>
      </span>

      <span className="bookcard__title">{folder.name}</span>

      <span className="bookcard__foot">
        <ProgressLine ratio={finished / books.length} />
        <span className="bookcard__status">{status}</span>
      </span>
    </button>
  );
}

/** `shelfName` hides the author line when it would only repeat the folder you
 *  are in: the scanner takes a book's author from its parent folder's name.
 *  `where` names the book's folder, for search results found outside it. */
function BookCard({
  book,
  shelfName,
  where,
}: {
  book: Book;
  shelfName?: string | undefined;
  where?: FolderRef | undefined;
}) {
  const entry = useApp((s) => s.progress.books[book.id]);
  const navigate = useApp((s) => s.navigate);
  const isCurrent = useApp((s) => s.currentBookId === book.id);
  const playing = useApp((s) => s.playing);
  const coverUrl = useCoverUrl(book);

  const ratio = bookRatio(book, entry);
  const total = totalDuration(book);
  const remaining = remainingSeconds(book, entry);
  // Figures worked out from file sizes, until every file has been measured.
  const approx = allDurationsKnown(book) ? '' : '~';

  const open = (event: React.MouseEvent<HTMLElement>) => {
    // The cover flies from here into the player (lib/transition.ts).
    setArtSource(event.currentTarget.querySelector<HTMLElement>('[data-art]'));
    navigate({ name: 'player', bookId: book.id });
    void openBook(book, { autoplay: false });
  };

  const status =
    entry?.finished ? 'Finished'
    : hasStarted(entry) && total > 0 ? timeLeft(remaining, approx)
    : hasStarted(entry) && entry ? whereYouAre(book, entry.trackIndex, entry.positionSec)
    : total > 0 ? approx + formatTime(total)
    : `${book.tracks.length} ${book.tracks.length === 1 ? 'file' : 'files'}`;

  return (
    <button
      type="button"
      onClick={open}
      className="tile press bookcard"
      data-current={isCurrent || undefined}
    >
      <span className="bookcard__frame" data-art={book.id}>
        <Cover title={book.title} url={coverUrl} />
        {isCurrent && <Equalizer playing={playing} />}
        {entry?.finished && !isCurrent && (
          <span className="bookcard__done" aria-hidden="true">
            <CheckIcon size={16} />
          </span>
        )}
      </span>

      <span className="bookcard__title">{book.title}</span>
      {book.author && book.author !== shelfName && book.author !== where?.name && (
        <span className="bookcard__author">{book.author}</span>
      )}
      {where && (
        <span className="bookcard__where">
          <FolderIcon size={13} />
          {where.name}
        </span>
      )}

      <span className="bookcard__foot">
        <ProgressLine ratio={ratio} />
        <span className="bookcard__status">{status}</span>
      </span>
    </button>
  );
}

/** "~2h 10m left", or — at or past the known end without being marked
 *  finished, which estimated lengths allow — "Nearly finished" rather than
 *  nothing. */
function timeLeft(seconds: number, approx: string): string {
  return seconds >= 1 ? approx + formatRemaining(seconds) : 'Nearly finished';
}

/** For a started book whose length is still being measured: say where you
 *  are, rather than nothing. */
function whereYouAre(book: Book, trackIndex: number, positionSec: number): string {
  return book.tracks.length > 1 ?
      `Part ${trackIndex + 1} of ${book.tracks.length}`
    : `${formatTime(positionSec)} in`;
}

function LibrarySkeleton() {
  return (
    <ul className="grid" aria-hidden="true">
      {Array.from({ length: 6 }, (_, i) => (
        <li key={i}>
          <div className="tile bookcard bookcard--skeleton">
            <span className="bookcard__frame" />
            <span className="skeleton-line" style={{ width: '76%' }} />
            <span className="skeleton-line" style={{ width: '48%' }} />
          </div>
        </li>
      ))}
    </ul>
  );
}
